/**
 * Build it with DANI, in the DANI chat (docs/pdlc/builder.md). The pattern is
 * feasibility's run-in-the-chat (docs/pdlc/feasibility-dani-strategist.md):
 * rules from a plan-template row ride in as pre-seeded history; DANI writes one
 * fenced `survey-draft` block; Studio reads it with the same rules as the
 * one-shot read (toDraftContent, validateDraft) and sends DANI its reading as a
 * hidden message. Nothing becomes a draft until the person presses Open in
 * Builder.
 *
 * Pure: no database, no network.
 */
import { HANDOFF_HEADER } from '@/lib/dani-handoff';
import type { PrimerMessage } from '@/lib/feasibility/dani/strategy';

import { questionCount, type BuilderDraftContent } from '../draft';
import { withoutStaffFields } from '../settings';
import type { DraftIssue } from '../validate';
import type { ChatChanges } from './chat-changes';
import { FEASIBILITY_IS_SEPARATE, SURVEY_STEP_HEADER } from './chat-flow';
import { shapeProblemsInWords } from './shape-words';
import { DaniDraftSchema, type DaniDraft } from './schema';
import type { DaniReading } from './to-draft';

/** The fenced block's language: ```survey-draft. */
export const SURVEY_DRAFT_LANGUAGE = 'survey-draft';
/** Longest block Studio reads (a 300-question survey fits well inside). */
export const MAX_BLOCK_CHARS = 400_000;

/** The first line of the rules message: the chat and transcripts leave it out. */
export const BUILDER_PRIMER_HEADER = 'SURVEY BUILDER RULES FOR DANI (not shown to the person)';
export const BUILDER_PRIMER_ACK =
    "Understood. I will work the survey out with you, hand the writing to Studio's survey author with a survey-start or survey-change block, explain what Studio sends back using only its numbers, and leave opening it in Builder to you.";
/** Acknowledgements earlier conversations were primed with, still hidden when reopened. */
const EARLIER_ACKS = [
    'Understood. I will work the survey out with you, write it as a survey-draft block, fix what Studio finds using only its numbers, and leave opening it in Builder to you.',
];

/** Heads Studio's reading of a block, sent to DANI as a hidden message. */
export const DRAFT_BRIEF_HEADER = 'SURVEY DRAFT FROM STUDIO';
/** Heads a survey the person already has, when they ask DANI about it from Builder. */
export const SURVEY_BRIEF_HEADER = 'SURVEY FROM BUILDER';

function textOf(content: unknown): string {
    if (typeof content === 'string') return content;
    if (!Array.isArray(content)) return '';
    return content
        .map((b) =>
            b && typeof b === 'object' && (b as { type?: unknown }).type === 'text'
                ? String((b as { text?: unknown }).text ?? '')
                : '',
        )
        .join('\n');
}

/**
 * This conversation already holds a survey Studio read (a draft on a card, saved
 * or not): Studio's brief of it names it on a `Survey:` line. A change can then
 * be written from the conversation; with none, there is nothing to change
 * (EDGE E11), and an unsaved draft in the chat still can be (EDGE R06).
 */
export function hasSurveyInChat(
    messages: readonly { role?: unknown; content: unknown }[],
): boolean {
    return messages.some((m) => {
        const text = textOf(m.content);
        // DANI handed a survey over earlier in this chat. Its own reply is always in the list;
        // Studio's brief is sent silently and is there only once the chat is reopened (R06, live).
        if (m.role === 'assistant') return /```survey-(start|draft)\b/.test(text);
        if (m.role !== 'user') return false;
        return text.trimStart().startsWith(DRAFT_BRIEF_HEADER) && /\nSurvey: /.test(text);
    });
}

/** True for the Builder rules message or DANI's acknowledgement of them. */
export function isBuilderPrimer(message: { role?: unknown; content: unknown }): boolean {
    const text = textOf(message.content).trim();
    if (message.role === 'user') return text.startsWith(BUILDER_PRIMER_HEADER);
    if (message.role === 'assistant')
        return text === BUILDER_PRIMER_ACK || EARLIER_ACKS.includes(text);
    return false;
}

/**
 * Messages the chat never shows: the rules, their acknowledgement, Studio's
 * readings of DANI's blocks, its notes on each step of the survey after it is
 * saved, and the hand-off notes. Saved history drops a
 * message's hidden mark, so a reopened conversation needs this to keep them
 * out (feasibility's isHiddenFeasibilityMessage, f48a9a28f). Transcripts keep
 * the readings, as the record of what DANI was given.
 */
export function isHiddenBuilderMessage(message: { role?: unknown; content: unknown }): boolean {
    if (isBuilderPrimer(message)) return true;
    if (message.role !== 'user') return false;
    const text = textOf(message.content).trim();
    return (
        text.startsWith(DRAFT_BRIEF_HEADER) ||
        text.startsWith(SURVEY_STEP_HEADER) ||
        text.startsWith(HANDOFF_HEADER)
    );
}

/**
 * The messages a Build-it-with-DANI conversation starts with, before the
 * person's own request: DANI's rules and its acknowledgement, sent as
 * pre-seeded history and never shown. Feasibility needs no rules of its own
 * here: the draft card runs the engine for staff.
 */
export function builderPrimer(template: { instructions: string }, today: string): PrimerMessage[] {
    const rules = [
        BUILDER_PRIMER_HEADER,
        '',
        template.instructions,
        '',
        `Today is ${today}. The next message is the request.`,
    ].join('\n');
    return [
        { role: 'user', content: [{ type: 'text', text: rules }] },
        { role: 'assistant', content: [{ type: 'text', text: BUILDER_PRIMER_ACK }] },
    ];
}

const asArray = (v: unknown) => (Array.isArray(v) ? v : []);
const asString = (v: unknown, fallback = '') => (typeof v === 'string' ? v : fallback);
const asNumber = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const asBool = (v: unknown) => (typeof v === 'boolean' ? v : null);

/**
 * Fills what DANI left out of a chat block, so a block that omits an empty
 * field still reads. The one-shot read uses structured output, which always
 * sends every field; a chat block is hand-written JSON.
 */
function withDefaults(raw: Record<string, unknown>): unknown {
    return {
        title: asString(raw.title),
        target: asNumber(raw.target),
        fieldDays: asNumber(raw.fieldDays),
        incidenceRate: asNumber(raw.incidenceRate),
        mediaCapture: asBool(raw.mediaCapture),
        ageMin: asNumber(raw.ageMin),
        ageMax: asNumber(raw.ageMax),
        genders: asArray(raw.genders),
        states: asArray(raw.states),
        stated: asArray(raw.stated),
        assumptions: asArray(raw.assumptions),
        openQuestions: asArray(raw.openQuestions),
        questions: asArray(raw.questions).map((q) => {
            const o = q && typeof q === 'object' ? (q as Record<string, unknown>) : {};
            return {
                id: asString(o.id),
                text: asString(o.text),
                type: o.type,
                options: asArray(o.options),
                rows: asArray(o.rows),
                randomize: asBool(o.randomize) ?? false,
                sliderMin: asNumber(o.sliderMin),
                sliderMax: asNumber(o.sliderMax),
                sliderMinLabel: typeof o.sliderMinLabel === 'string' ? o.sliderMinLabel : null,
                sliderMaxLabel: typeof o.sliderMaxLabel === 'string' ? o.sliderMaxLabel : null,
                terminateOn: asArray(o.terminateOn),
                showIf: o.showIf ?? null,
            };
        }),
    };
}

export type BlockRead = { ok: true; draft: DaniDraft } | { ok: false; error: string };

/** A survey-draft block's JSON, read the way the one-shot read reads DANI's answer. */
export function readSurveyBlock(text: string): BlockRead {
    if (text.length > MAX_BLOCK_CHARS)
        return { ok: false, error: 'The survey DANI wrote is too long to read.' };
    let raw: unknown;
    try {
        raw = JSON.parse(text);
    } catch {
        return { ok: false, error: 'DANI wrote the survey in a form Studio could not read.' };
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return { ok: false, error: 'DANI wrote the survey in a form Studio could not read.' };
    const parsed = DaniDraftSchema.safeParse(withDefaults(raw as Record<string, unknown>));
    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        const said = issue?.path?.length
            ? shapeProblemsInWords(`${issue.path.join('.')}: ${issue.message}`)
            : (issue?.message ?? 'it is not a survey');
        return { ok: false, error: `Studio could not read the survey. ${said}.` };
    }
    return { ok: true, draft: parsed.data };
}

/**
 * The three forms of a survey-draft block:
 *
 * - `simple`: a new survey in the one-shot read's fields, plus `set` for the
 *   sections that name no question (QUESTION_FREE_SECTIONS).
 * - `full`: a new survey in Builder's own shape, `{ "survey": { ... } }`, where
 *   caps, follow-ups and translations can name its questions.
 * - `edit`: changes to a saved draft, `{ "draftId", "baseVersion", "set",
 *   "questions": { "upsert", "remove", "order" } }`, in Builder's own shape.
 */
export type ChatBlock =
    | { kind: 'simple'; draft: DaniDraft; changes: ChatChanges }
    | { kind: 'full'; survey: Record<string, unknown> }
    | { kind: 'edit'; draftId: string; baseVersion: number; changes: ChatChanges };

const isObject = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

function changesOf(raw: Record<string, unknown>): ChatChanges {
    return {
        ...(isObject(raw.set) ? { set: raw.set } : {}),
        ...(isObject(raw.questions)
            ? { questions: raw.questions as ChatChanges['questions'] }
            : {}),
    };
}

export function readChatBlock(
    text: string,
): { ok: true; block: ChatBlock } | { ok: false; error: string } {
    if (text.length > MAX_BLOCK_CHARS)
        return { ok: false, error: 'The survey DANI wrote is too long to read.' };
    let raw: unknown;
    try {
        raw = JSON.parse(text);
    } catch {
        return { ok: false, error: 'DANI wrote the survey in a form Studio could not read.' };
    }
    if (!isObject(raw))
        return { ok: false, error: 'DANI wrote the survey in a form Studio could not read.' };
    if (raw.draftId != null) {
        const baseVersion = Number(raw.baseVersion);
        if (typeof raw.draftId !== 'string' || !Number.isInteger(baseVersion) || baseVersion < 1)
            return {
                ok: false,
                error: 'A change to a saved survey names its draftId and the baseVersion it starts from.',
            };
        return {
            ok: true,
            block: { kind: 'edit', draftId: raw.draftId, baseVersion, changes: changesOf(raw) },
        };
    }
    if (isObject(raw.survey)) return { ok: true, block: { kind: 'full', survey: raw.survey } };
    const simple = readSurveyBlock(text);
    if (!simple.ok) return simple;
    return {
        ok: true,
        block: {
            kind: 'simple',
            draft: simple.draft,
            changes: isObject(raw.set) ? { set: raw.set } : {},
        },
    };
}

/** Longest survey JSON a brief carries for DANI to work from. */
export const MAX_NATIVE_CHARS = 60_000;

/**
 * The survey as Builder holds it, for DANI's next block: compact JSON, without
 * the staff sections for a client, or a note when it is too long to carry.
 */
export function nativeJson(content: BuilderDraftContent, staff: boolean): string {
    // The draft's own bookkeeping is not DANI's to write.
    const rest: Partial<BuilderDraftContent> = { ...content };
    delete rest.schemaVersion;
    delete rest.targetOrgId;
    delete rest.questionSeq;
    const shown = staff ? rest : withoutStaffFields(rest);
    const json = JSON.stringify(shown);
    return json.length <= MAX_NATIVE_CHARS
        ? json
        : `(The survey is ${json.length.toLocaleString('en-US')} characters, too long to carry here. Change it with set and questions.upsert, naming questions by the ids above.)`;
}

/** What Studio added to DANI's own assumptions while reading the block. */
export function studioNotes(parsed: DaniDraft, reading: DaniReading): string[] {
    return reading.assumptions.slice(parsed.assumptions.length);
}

const fmt = (n: number) => n.toLocaleString('en-US');

function audienceLine(content: BuilderDraftContent): string {
    const parts = content.audience.criteria.flatMap((c) => {
        if (c.kind !== 'demographic') return [c.kind.replace(/_/g, ' ')];
        return [
            ...(c.age ? [`ages ${c.age.min ?? 13}-${c.age.max ?? 99}`] : []),
            ...(c.genders?.length ? [c.genders.join(' or ')] : []),
            ...(c.states?.length ? [`in ${c.states.join(', ')}`] : []),
            ...(c.dmas?.length ? [`${c.dmas.length} markets`] : []),
            ...(c.regions?.length ? [c.regions.join(', ')] : []),
            ...(c.zips?.codes.length ? [`${c.zips.codes.length} zip codes`] : []),
        ];
    });
    return parts.length ? parts.join(', ') : 'everyone on the panel';
}

/**
 * The question count the card shows (`questionCount`: a grid's rows are part of
 * the grid), with the rows named so DANI says the same number as the card.
 */
function countWords(content: BuilderDraftContent): string {
    const n = questionCount(content);
    const rows = content.questions.length - n;
    return `${n} ${n === 1 ? 'question' : 'questions'}${rows ? ` (grids hold ${rows} more rows; a row is not a question)` : ''}`;
}

function fieldingLine(content: BuilderDraftContent): string {
    const f = content.fielding;
    return [
        f.target == null ? 'as many completes as possible' : `${fmt(f.target)} completes`,
        f.fieldDays == null
            ? 'field days not set'
            : `${f.fieldDays} ${f.fieldDays === 1 ? 'day' : 'days'} in field`,
        f.incidenceRate == null
            ? 'share who qualify not set'
            : `${Math.round(f.incidenceRate * 100)}% expected to qualify`,
    ].join(', ');
}

function issueLines(issues: readonly DraftIssue[]): string[] {
    const errors = issues.filter((i) => i.severity === 'error');
    const warnings = issues.filter((i) => i.severity === 'warning');
    const line = (i: DraftIssue) => `- ${i.questionId ? `${i.questionId}: ` : ''}${i.message}`;
    return [
        errors.length
            ? `Problems that stop it from publishing (${errors.length}):`
            : // Builder's own checks only. Feasibility is advised, never required, so a survey
              // that cannot reach its target still publishes (UAT F45).
              `Builder finds nothing in the survey that stops it from publishing. That is not whether it can reach its target: ${FEASIBILITY_IS_SEPARATE}`,
        ...errors.slice(0, 40).map(line),
        ...(warnings.length ? [`Warnings (${warnings.length}):`] : []),
        ...warnings.slice(0, 20).map(line),
    ];
}

/**
 * Studio's reading of a survey-draft block, for DANI to explain and fix. Its
 * feasibility half, when staff ran one, is the feasibility chat's own brief,
 * appended by the card.
 */
export function draftBriefForDani(args: {
    reading: DaniReading;
    notes: readonly string[];
    issues: readonly DraftIssue[];
    feasibility: 'checking' | 'not_checked' | { missing: string[] } | null;
    /** A change to a saved survey: which one, and what the change does. */
    edit?: {
        draftId: string;
        baseVersion: number;
        changes: readonly string[];
        /** The saved survey's status: a published one is changed in Builder, not saved here. */
        status?: string;
    } | null;
    /** The survey as Builder holds it after this block (nativeJson). */
    native?: string | null;
    /** Claude wrote it from DANI's hand-off, rather than DANI in a survey-draft block. */
    authoredByClaude?: boolean;
}): string {
    const { reading, notes, issues, feasibility, edit, native, authoredByClaude } = args;
    const c = reading.content;
    const lines = [
        `${DRAFT_BRIEF_HEADER} (not from the person; they do not see this message)`,
        '',
        ...(authoredByClaude
            ? [
                  'The survey from your hand-off is on the card under your message; to the person it is your draft (never name a survey author or say Studio wrote it). Tell the person what it holds and what is still open, using only what is below. For a change, write a survey-change block with their request in words.',
                  '',
              ]
            : []),
        `Survey: ${c.title}. ${countWords(c)}. Audience: ${audienceLine(c)}. ${fieldingLine(c)}.`,
        '',
        ...(edit
            ? [
                  `A change to the saved survey ${edit.draftId}, from version ${edit.baseVersion}:`,
                  ...(edit.changes.length
                      ? edit.changes.map((w) => `- ${w}`)
                      : ['- nothing changes']),
                  '',
              ]
            : []),
        ...(notes.length
            ? [
                  'What Studio changed or left out while reading it:',
                  ...notes.map((n) => `- ${n}`),
                  '',
              ]
            : []),
        ...issueLines(issues),
        '',
        ...(reading.openQuestions.length
            ? ['Still open with the person:', ...reading.openQuestions.map((q) => `- ${q}`), '']
            : []),
        feasibility === 'checking'
            ? 'Feasibility: the engine is checking it; its result follows in this message.'
            : feasibility === 'not_checked'
              ? 'Feasibility: not checked in the chat. Builder checks it when the survey is opened there.'
              : feasibility && 'missing' in feasibility
                ? `Feasibility: not checked yet, it needs ${feasibility.missing.join(' and ')}.`
                : '',
        '',
        ...(native
            ? [
                  "The survey as Builder holds it (write your next block from this; question ids are Builder's):",
                  native,
                  '',
              ]
            : []),
        edit && edit.status === 'published'
            ? 'Nothing is saved, and it cannot be saved from the card: the survey is published. The person opens it in Builder from the card, chooses Change this survey there, makes the change, and publishes it again. Answers already collected stay as they were. Never tell them to save it from the card.'
            : edit
              ? `Nothing is saved. The person saves it as version ${edit.baseVersion + 1} from the card when they are ready.`
              : 'Nothing is saved. The person saves it from the card when they are ready (or opens it in Builder); the card then takes it through checking, preview, feasibility, price, publish and launch in this chat.',
    ];
    return lines
        .filter((l, i, all) => !(l === '' && all[i - 1] === ''))
        .join('\n')
        .trim();
}

const MAX_BRIEF_QUESTIONS = 80;

/** A survey the person has in Builder, for "Ask DANI about this survey". */
export function surveyBriefForDani(args: {
    content: BuilderDraftContent;
    issues: readonly DraftIssue[];
    status: string;
    link: string;
    draftId: string;
    versionNo: number;
    staff: boolean;
}): string {
    const { content, issues, status, link, draftId, versionNo, staff } = args;
    const questions = content.questions.slice(0, MAX_BRIEF_QUESTIONS).map((q) => {
        const answers = q.options.length
            ? ` Answers: ${q.options
                  .slice(0, 15)
                  .map((o) => `${o.option_text}${o.terminate ? ' (ends the survey)' : ''}`)
                  .join(
                      ' | ',
                  )}${q.options.length > 15 ? ` and ${q.options.length - 15} more` : ''}.`
            : '';
        const rule = q.show_if ? ' Shown only after an earlier answer.' : '';
        return `- ${q.question_id} (${q.question_type}): ${q.question_text.slice(0, 300)}${answers}${rule}`;
    });
    return [
        `${SURVEY_BRIEF_HEADER} (not from the person; they asked for help with this survey)`,
        '',
        `Survey: ${content.title} (${status}). Open in Builder: ${link}`,
        `Audience: ${audienceLine(content)}. ${fieldingLine(content)}.`,
        '',
        `Questions (${countWords(content)}; each entry below is one item):`,
        ...questions,
        ...(content.questions.length > MAX_BRIEF_QUESTIONS
            ? [`- and ${content.questions.length - MAX_BRIEF_QUESTIONS} more`]
            : []),
        '',
        ...issueLines(issues),
        '',
        `draftId: ${draftId}, version ${versionNo}. The survey as Builder holds it:`,
        nativeJson(content, staff),
        '',
        status === 'draft'
            ? `Ask what the person wants help with. To change this survey, write a survey-draft block with "draftId": "${draftId}", "baseVersion": ${versionNo}, and the changes in set and questions; the person saves it from the card. Without a draftId, a block opens as a new survey.`
            : `This survey is ${status}: changes are saved only after the person chooses Change this survey in Builder. A survey-draft block without a draftId opens as a new survey.`,
    ].join('\n');
}

/** The hand-off blocks DANI writes for Claude to author (DANI and Claude side by side). */
export const SURVEY_TASK_LANGUAGES = ['survey-start', 'survey-change'] as const;
export type SurveyTaskLanguage = (typeof SURVEY_TASK_LANGUAGES)[number];

/** What DANI handed over: the ask in words, and for a change the draft it names (or null). */
export function readSurveyTask(
    language: SurveyTaskLanguage,
    content: string,
): { ask: string; draftId: string | null } | null {
    try {
        const v = JSON.parse(content) as { summary?: unknown; ask?: unknown; draftId?: unknown };
        const ask =
            typeof v.ask === 'string' ? v.ask : typeof v.summary === 'string' ? v.summary : '';
        if (!ask.trim()) return null;
        return {
            ask: ask.trim().slice(0, 4000),
            draftId:
                language === 'survey-change' && typeof v.draftId === 'string' && v.draftId.trim()
                    ? v.draftId.trim()
                    : null,
        };
    } catch {
        return null;
    }
}

/**
 * The conversation as the person saw it, oldest first, for Claude to write
 * from: what the person and DANI said, never the rules or Studio's hidden
 * messages, with each DANI block left out.
 */
export function conversationText(
    messages: readonly { role?: unknown; content: unknown; silent?: boolean }[],
    isHidden: (m: { role?: unknown; content: unknown }) => boolean,
): string {
    const out: string[] = [];
    for (const m of messages) {
        if (m.silent || isHidden(m)) continue;
        if (m.role !== 'user' && m.role !== 'assistant') continue;
        const text = textOf(m.content)
            .replace(/```[a-z][\w-]*\s*\n[\s\S]*?(```|$)/g, '')
            .trim();
        if (text) out.push(`${m.role === 'user' ? 'PERSON' : 'DANI'}: ${text}`);
    }
    return out.join('\n\n');
}
